import { useTranslation } from "react-i18next";
import { iField, Integration } from "/app/src/models";
import { useCallback } from "react";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { Form, Input, Select, SubmitButton, Switch } from "formik-antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { iFieldService } from "/app/src/services";

interface FormValues {
  name: string;
  values: string;
  required: boolean;
}

/**
 * Formats the form values.
 * @param {FormValues} values - The form values.
 * @param {"order" | "line"} type - The type of the field.
 * @param {Function} getFieldName - Function to get the label for a field name.
 * @returns {object} The formatted form object.
 */
function formatForm(
  values: FormValues,
  type: "order" | "line",
  getFieldName: (value: string, type: "order" | "line") => string | undefined,
) {
  return {
    name: values.name,
    values:
      values.values === "" || values.values === null
        ? (getFieldName(values.name, type) ?? "") // Use getFieldName for default value
        : values.values,
    required: values.required,
  };
}

/**
 * NewOrderFieldsForm is a component that displays a form for adding new order fields.
 * @param {Object} props - The properties object.
 * @param {Integration} props.integration - Integration information.
 * @param {iField[]} props.fields - Array of fields.
 * @param {"order" | "line"} props.type - Type of the field.
 * @returns {JSX.Element} A form component for inputting new order fields.
 */
export default function NewOrderFieldsForm({
  integration,
  fields,
  type,
  fieldOptions,
}: {
  integration: Integration;
  fields: iField[];
  type: "order" | "line";
  fieldOptions: Array<{ label: string; value: string }>;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /**
   * Compares existing order fields with available field options.
   * @param {iField[]} orderFields - Array of current order fields.
   * @returns {Array} Available field options not present in the current order fields.
   */
  const compareOrderFields = useCallback(
    (orderFields: iField[]) => {
      const orderFieldValues = orderFields?.map((field) => field?.name);
      return fieldOptions.filter(
        (option) => !orderFieldValues.includes(option.value),
      );
    },
    [fieldOptions],
  );

  // Function to get the corresponding field name
  const getFieldName = useCallback(
    (value: string) => {
      return fieldOptions.find((option) => option.value === value)?.label;
    },
    [fieldOptions],
  );

  const { mutateAsync: createField } = useMutation({
    mutationFn: (values: iField) => {
      return iFieldService
        .createSingle({
          ...values,
          integrationId: integration.id,
        })
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        [`${type}Fields`, integration.id],
        (oldData: { fields: iField[] }) => {
          const newField = response.field;
          return {
            fields: [...oldData.fields, newField],
          };
        },
      );
    },
  });

  /**
   * Handles form submission.
   * @param {FormValues} values - The values submitted from the form.
   * @param {FormikHelpers<FormValues>} actions - Formik actions for managing form state.
   * @returns {Promise} A promise that resolves after the field is created and the form is reset.
   */
  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      return await createField({
        level: type,
        ...formatForm(values, type, getFieldName), // Pass type and getFieldName
      }).then(() => {
        actions.resetForm();
      });
    },
    [createField, type, getFieldName],
  );

  const stopPropagation = useCallback((e: React.FocusEvent<HTMLElement>) => {
    e.stopPropagation();
  }, []);

  /**
   * Renders the form using Formik props.
   * @param {FormikProps<FormValues>} props - The Formik properties.
   * @returns {JSX.Element} The form layout element.
   */
  const orderFieldsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ isSubmitting, dirty, values, isValid }) => (
        <Form layout="vertical">
          <div className="grid grid-cols-3 gap-x-6 pt-[20px]">
            <Form.Item name="name" hasFeedback={false} className="w-[300px]">
              <Select
                name="name"
                placeholder="Add Field"
                size="large"
                options={compareOrderFields(fields)}
                onFocus={stopPropagation}
              />
            </Form.Item>
            {values.name !== "" && (
              <>
                <div className="w-[300px]">
                  <Form.Item hasFeedback={false} name="values">
                    <Input
                      name="values"
                      placeholder={getFieldName(values.name)}
                      size="large"
                    />
                  </Form.Item>
                </div>
                <div
                  className={`flex ${values.name === "name" ? "justify-end" : "justify-between"}`}
                >
                  {values.name !== "name" && (
                    <Form.Item
                      hasFeedback={false}
                      name="required"
                      className="!m-0"
                    >
                      <Switch
                        name="required"
                        className="!m-0"
                        aria-label="Required field"
                      />
                    </Form.Item>
                  )}
                  <div>
                    <SubmitButton
                      disabled={isSubmitting || !dirty || !isValid}
                      className="!m-0 "
                      block
                    >
                      {t("translation:save")}
                    </SubmitButton>
                  </div>
                </div>
              </>
            )}
          </div>
        </Form>
      ),
      [compareOrderFields, fields, getFieldName, stopPropagation, t],
    );

  return (
    <Formik
      initialValues={{
        name: "",
        values: "",
        required: false,
      }}
      onSubmit={onSubmit}
      component={orderFieldsForm}
      enableReinitialize
    />
  );
}
